import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import { Check, Copy } from "lucide-react";
import "highlight.js/styles/atom-one-light.css";

// Allow a tiny set of inline HTML (mainly <br>, which models emit inside
// markdown table cells) while still stripping anything dangerous like
// <script>, event handlers, etc. Everything not whitelisted is removed.
const MARKDOWN_SANITIZE_SCHEMA = {
    ...defaultSchema,
    tagNames: [...new Set([...(defaultSchema.tagNames || []), "br"])],
};

function flattenNodeText(node) {
    if (typeof node === "string" || typeof node === "number") {
        return String(node);
    }
    if (Array.isArray(node)) {
        return node.map(flattenNodeText).join("");
    }
    if (node && typeof node === "object") {
        return flattenNodeText(node.props?.children);
    }
    return "";
}

export default function MarkdownMessageRenderer({
    content,
    isUser,
    hideCanvasCodeBlocks = false,
}) {
    const [copiedKey, setCopiedKey] = useState(null);
    const normalizedContent = String(content || "");

    const copyToClipboard = async (code, key) => {
        if (!navigator?.clipboard?.writeText) return;
        try {
            await navigator.clipboard.writeText(code);
            setCopiedKey(key);
            setTimeout(() => setCopiedKey(null), 1600);
        } catch {
            setCopiedKey(null);
        }
    };

    return (
        <div className={`w-full markdown-content break-words ${isUser ? "user-message" : "assistant-message"}`}>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw, [rehypeSanitize, MARKDOWN_SANITIZE_SCHEMA], rehypeHighlight]}
                components={{
                    h1: ({ children }) => <h1 className="text-xl font-semibold mt-2 mb-3">{children}</h1>,
                    h2: ({ children }) => <h2 className="text-lg font-semibold mt-2 mb-2">{children}</h2>,
                    h3: ({ children }) => <h3 className="text-base font-semibold mt-2 mb-2">{children}</h3>,
                    p: ({ children }) => <p className="mb-2 last:mb-0 leading-relaxed whitespace-pre-wrap">{children}</p>,
                    ul: ({ children }) => <ul className="list-disc pl-5 mb-2 space-y-1">{children}</ul>,
                    ol: ({ children }) => <ol className="list-decimal pl-5 mb-2 space-y-1">{children}</ol>,
                    li: ({ children }) => <li className="leading-relaxed">{children}</li>,
                    blockquote: ({ children }) => (
                        <blockquote
                            className={`my-3 border-l-4 pl-3 italic ${
                                isUser ? "border-white/40 text-white/90" : "border-slate-300 text-slate-600"
                            }`}
                        >
                            {children}
                        </blockquote>
                    ),
                    hr: () => <hr className={`my-4 ${isUser ? "border-white/30" : "border-slate-200"}`} />,

                    table: ({ children }) => (
                        <div className="overflow-x-auto my-4 rounded-xl border border-slate-200/50">
                            <table className={`w-full border-collapse text-sm ${isUser ? "text-white" : "text-slate-700"}`}>
                                {children}
                            </table>
                        </div>
                    ),

                    thead: ({ children }) => (
                        <thead className={`${isUser ? 'bg-white/10 text-white' : 'bg-slate-50 text-slate-700'} font-medium border-b border-slate-200/20`}>
                            {children}
                        </thead>
                    ),
                    th: ({ children }) => (
                        <th className="px-3 py-2 text-left border-b border-slate-200/20 align-top">
                            {children}
                        </th>
                    ),
                    td: ({ children }) => (
                        <td className="px-3 py-2 border-b border-slate-200/10 align-top break-words">
                            {children}
                        </td>
                    ),
                    tr: ({ children }) => (
                        <tr className={`${isUser ? 'hover:bg-white/5' : 'hover:bg-slate-50'} transition-colors`}>
                            {children}
                        </tr>
                    ),

                    code({ inline, className, children, ...props }) {
                        const codeText = flattenNodeText(children).replace(/\n$/, "");
                        const match = /language-([a-z0-9_-]+)/i.exec(className || "");
                        const language = match?.[1] || "text";
                        const isBlock = inline === false || codeText.includes("\n");
                        const copyKey = `${language}:${codeText.slice(0, 40)}`;
                        const isCanvasLang =
                            !isUser &&
                            (["html", "htm", "jsx", "tsx", "javascript", "js", "typescript", "ts"].includes(
                                language.toLowerCase()
                            ) ||
                            // In canvas mode, also hide unlabeled/text blocks that look like HTML
                            (hideCanvasCodeBlocks && (language === "text" || language === "") &&
                              /<[a-zA-Z]/.test(codeText)));
                        const shouldHideCanvasBlockInChat = hideCanvasCodeBlocks && isCanvasLang;

                        if (isBlock) {
                            // Canvas code blocks are hidden in chat — the artifact card
                            // is rendered by ChatBubble from the message data instead.
                            if (shouldHideCanvasBlockInChat) {
                                return null;
                            }
                            return (
                                <div className="relative my-4 rounded-2xl w-full overflow-hidden border border-slate-200 bg-slate-50 shadow-sm">
                                    <div className="flex justify-between items-center px-4 py-2.5 bg-slate-200/70 border-b border-slate-200">
                                        <span className="text-xs font-semibold text-slate-700">
                                            {language}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => copyToClipboard(codeText, copyKey)}
                                            className="h-7 w-7 inline-flex items-center justify-center rounded-md text-slate-500 hover:text-slate-700 hover:bg-slate-300/70 transition"
                                            aria-label="Copy code"
                                            title={copiedKey === copyKey ? "Copied" : "Copy code"}
                                        >
                                            {copiedKey === copyKey ? <Check size={14} /> : <Copy size={14} />}
                                        </button>
                                    </div>
                                    <div className="px-4 py-3 text-sm overflow-x-auto font-mono leading-relaxed">
                                        <code className={className || "language-text"} {...props}>
                                            {children}
                                        </code>
                                    </div>
                                </div>
                            );
                        }

                        return (
                            <code
                                className={`px-1.5 py-0.5 rounded text-sm font-mono break-words ${
                                isUser
                                    ? "bg-white/20 text-white border border-white/10"
                                    : "bg-slate-100 text-pink-600 border border-slate-200"
                            }`}
                                {...props}
                            >
                                {children}
                            </code>
                        );
                    },

                    a: ({ children, ...props }) => (
                        <a
                            {...props}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`underline decoration-1 underline-offset-2 break-all ${
                                isUser
                                    ? "text-white decoration-white/50 hover:decoration-white"
                                    : "text-indigo-600 decoration-indigo-300 hover:decoration-indigo-600"
                            }`}
                        >
                            {children}
                        </a>
                    ),
                }}
            >
                {normalizedContent}
            </ReactMarkdown>
        </div>
    );
}
